/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import React, { ComponentProps, FC, HTMLAttributes } from 'react';
import { useField, FieldConfig } from 'formik';

import { Body } from '@airwallex/typography';
import { useMobile } from '@/hooks/useMediaQuery';

import {
  labelContainerCss,
  labelCss,
  inputCss,
  inputContainerCss,
  prefixCss,
  suffixCss,
  errorCss,
  hintCss,
} from './styles';

interface InputProps {
  name: string;
  type?: string;
  placeholder?: string;
  label?: React.ReactNode;
  optional?: boolean;
  optionalDisplayText?: React.ReactNode;
  hint?: React.ReactNode;
  className?: string;
  error?: string;
  validate?: FieldConfig['validate'];
  prefixComponent?: React.ReactNode;
  suffixComponent?: React.ReactNode;
  errorComponent?: React.ReactNode;
}

const Hint: FC<HTMLAttributes<HTMLDivElement>> = ({ children, ...rest }) => (
  <div
    {...rest}
    css={css`
      position: relative;
      line-height: initial;
      height: 16px;
    `}
  >
    <svg
      width="17"
      height="16"
      viewBox="0 0 17 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      css={css`
        cursor: pointer;
        & + span {
          visibility: hidden;
          width: 160px;
          transform: translateX(-50%);
          background-color: black;
          color: #fff;
          text-align: center;
          border-radius: 6px;
          padding: 8px 4px;
          position: absolute;
          z-index: 1;
          top: 150%;
          left: 50%;
          font-size: 12px;
        }
        & + span::after {
          content: ' ';
          position: absolute;
          bottom: 100%;
          left: 50%;
          margin-left: -5px;
          border-width: 5px;
          border-style: solid;
          border-color: transparent transparent black transparent;
        }
        &:hover + span {
          visibility: visible;
        }
      `}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.833008 8C0.833008 12.4183 4.41473 16 8.83301 16C13.2513 16 16.833 12.4183 16.833 8C16.833 3.58172 13.2513 0 8.83301 0C4.41473 0 0.833008 3.58172 0.833008 8ZM14.833 8C14.833 11.3137 12.1467 14 8.83301 14C5.5193 14 2.83301 11.3137 2.83301 8C2.83301 4.68629 5.5193 2 8.83301 2C12.1467 2 14.833 4.68629 14.833 8ZM7.83301 8C7.83301 7.44772 8.28072 7 8.83301 7C9.38529 7 9.83301 7.44772 9.83301 8V11C9.83301 11.5523 9.38529 12 8.83301 12C8.28072 12 7.83301 11.5523 7.83301 11V8ZM8.83301 4C8.28072 4 7.83301 4.44772 7.83301 5C7.83301 5.55228 8.28072 6 8.83301 6C9.38529 6 9.83301 5.55228 9.83301 5C9.83301 4.44772 9.38529 4 8.83301 4Z"
        fill="#B0B6BF"
      />
    </svg>
    <span>{children}</span>
  </div>
);

const Index = React.forwardRef<HTMLInputElement, InputProps & ComponentProps<'input'>>((props, ref) => {
  const {
    label,
    name,
    optional,
    optionalDisplayText,
    hint,
    prefixComponent,
    suffixComponent,
    className,
    errorComponent,
    validate,
    ...others
  } = props;
  const [field, meta] = useField({ name, validate });
  const isMobile = useMobile();

  return (
    <div
      className={className}
      css={css`
        padding: 8px;
      `}
    >
      <div css={labelContainerCss}>
        {label && (
          <label htmlFor={name} css={labelCss}>
            <Body>{label}</Body>
            {optional && <Body variant="100.Disabled">{optionalDisplayText || 'Optional'}</Body>}
            {!isMobile && hint && <Hint>{hint}</Hint>}
          </label>
        )}
      </div>
      <div css={inputContainerCss}>
        <Body css={prefixCss}>{prefixComponent}</Body>
        <input
          ref={ref}
          {...field}
          {...others}
          onChange={(e) => {
            field?.onChange(e);
            if (others?.onChange) {
              others?.onChange(e);
            }
          }}
          css={inputCss(meta.touched, meta.error, prefixComponent, suffixComponent)}
        />
        <Body css={suffixCss}>{suffixComponent}</Body>
      </div>
      {isMobile && hint && <div css={hintCss}>{hint}</div>}
      {meta.touched && meta.error ? <div css={errorCss}>{errorComponent || meta.error}</div> : null}
    </div>
  );
});

Index.displayName = 'input';

export default Index;
